// Floating widget rendered while a Messenger Calling API call is in
// progress. Renders the remote audio stream, mute/hangup controls, and
// the customer's name/picture.
//
// Positioned to the left of Twilio's active widget so multiple
// simultaneous active calls across stacks don't overlap.

import React, { useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import MessageIcon from "@mui/icons-material/Message";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useMessengerCall } from "src/context/messengerCall/MessengerCallContext";

const MessengerActiveCallWidget: React.FC = () => {
  const {
    callState,
    activeCall,
    remoteStream,
    formattedDuration,
    isMuted,
    isRecording,
    isUploadingRecording,
    hangup,
    toggleMute,
  } = useMessengerCall();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  const isVisible = callState === "calling" || callState === "in-call";
  if (!isVisible || !activeCall) return null;

  const statusLabel =
    callState === "calling" ? "Connecting…" : formattedDuration || "Connected";
  const primary = activeCall.customerName || "Messenger user";

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        top: 80,
        // Sits to the LEFT of TwilioVoice's ActiveCallWidget (right: 40)
        // so both can be visible if an agent has cross-channel calls.
        right: 340,
        zIndex: 9999,
        p: 2,
        width: 280,
        borderRadius: 4,
        background: "rgba(255, 255, 255, 0.97)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 8px 32px rgba(24, 119, 242, 0.18)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Avatar
        src={activeCall.customerPicture || undefined}
        sx={{
          width: 64,
          height: 64,
          mb: 1,
          border: "2px solid #1877F2",
          bgcolor: "rgba(24, 119, 242, 0.08)",
        }}
      >
        <MessageIcon sx={{ color: "#1877F2", fontSize: 28 }} />
      </Avatar>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {primary}
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
        {statusLabel}
      </Typography>

      {/* Recording / uploading status row. Recording shows a pulsing
          red dot during the live call; uploading shows after hangup
          for a few seconds while the MediaRecorder blob streams to S3. */}
      {(isRecording || isUploadingRecording) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 1,
            color: isUploadingRecording ? "#1877F2" : "#F22F46",
          }}
        >
          {isUploadingRecording ? (
            <CloudUploadIcon sx={{ fontSize: 14 }} />
          ) : (
            <FiberManualRecordIcon
              sx={{
                fontSize: 12,
                animation: "messengerCallRecPulse 1.4s ease-in-out infinite",
                "@keyframes messengerCallRecPulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.35 },
                },
              }}
            />
          )}
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {isUploadingRecording ? "Uploading recording…" : "Recording"}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
        <Tooltip title={isMuted ? "Unmute" : "Mute"}>
          <IconButton
            onClick={toggleMute}
            sx={{
              bgcolor: isMuted ? "#1877F2" : "rgba(0,0,0,0.06)",
              color: isMuted ? "white" : "rgba(0,0,0,0.7)",
              "&:hover": {
                bgcolor: isMuted ? "#125ab8" : "rgba(0,0,0,0.12)",
              },
              width: 44,
              height: 44,
            }}
          >
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Hang up">
          <IconButton
            onClick={hangup}
            sx={{
              bgcolor: "#FF3B30",
              color: "white",
              "&:hover": { bgcolor: "#D70015" },
              width: 44,
              height: 44,
            }}
          >
            <CallEndIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} autoPlay />
    </Paper>
  );
};

export default MessengerActiveCallWidget;
